import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Menu list item visual variants
 */
export const menuListItemVariants = cva(
  // `flashka-menu-row` is a style-free MARKER: it lets `MenuHighlight` find the
  // row under the pointer, measure it, and read the `--menu-wash-rgb` each
  // variant publishes below.
  'flashka-menu-row ' +
    'group relative inline-flex flex-row items-center justify-start border border-transparent ' +
    'web:transition-[color,background-color,border-color,box-shadow,opacity] ' +
    'web:duration-100 web:ease-out ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0',
  {
    variants: {
      // FOCUS is `web:`-only — RN has no focus ring here. HOVER and PRESS are
      // mirrored with `native:*` at the same shade as their web twins:
      // `web:hover:` / `web:active:` are stripped on native, so without the
      // mirrors a Pencil hover (or a tap) on a row in a native Dropdown /
      // Select sheet gave no feedback at all.
      //
      // The `backdrop-blur` twins stay web-only — RN has no `backdrop-filter`.
      variant: {
        secondary:
          'bg-transparent web:[--menu-wash-rgb:var(--secondary-200-rgb)] ' +
          'web:hover:bg-secondary-200/20 web:active:bg-secondary-300/30 ' +
          'native:hover:bg-secondary-200/20 native:active:bg-secondary-300/30 ' +
          'web:hover:backdrop-blur-sm web:active:backdrop-blur-sm ' +
          'web:focus-visible:bg-secondary-200/20 web:focus-visible:backdrop-blur-sm ' +
          'web:focus-visible:ring-secondary-600 ',
        primary:
          'bg-transparent web:[--menu-wash-rgb:var(--primary-200-rgb)] ' +
          'web:hover:bg-primary-200/20 web:active:bg-primary-300/30 ' +
          'native:hover:bg-primary-200/20 native:active:bg-primary-300/30 ' +
          'web:hover:backdrop-blur-sm web:active:backdrop-blur-sm ' +
          'web:focus-visible:bg-secondary-200/20 web:focus-visible:backdrop-blur-sm ' +
          'web:focus-visible:ring-primary-950 ',
        error:
          'bg-transparent web:[--menu-wash-rgb:var(--red-200-rgb)] ' +
          'web:hover:bg-red-200/20 web:active:bg-red-300/30 ' +
          'native:hover:bg-red-200/20 native:active:bg-red-300/30 ' +
          'web:hover:backdrop-blur-sm web:active:backdrop-blur-sm ' +
          'web:focus-visible:ring-red-900 ',
      },
      size: {
        xl: 'h-14 pl-3   pr-3.5 gap-2 rounded-[18px]',
        l: 'h-12 pl-3   pr-3.5 gap-2 rounded-[18px]',
        m: 'h-11 pl-2.5 pr-3   gap-2 rounded-[14px]',
        s: 'h-8  pl-2   pr-2.5 gap-2 rounded-[12px]',
      },
      hasDescription: {
        true: '',
        false: '',
      },
      selected: {
        true: '',
        false: '',
      },
      disabled: {
        true: '',
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      // Inside a `<MenuHighlight>` list, one shared indicator paints the hover
      // and focus wash as it slides between rows — so the row must not paint
      // its own, or the two stack. `active:` deliberately stays: a press should
      // read instantly on the row the finger is on, not travel to it.
      slidingHighlight: {
        true:
          'web:hover:bg-transparent web:hover:backdrop-blur-none ' +
          'web:focus-visible:bg-transparent web:focus-visible:backdrop-blur-none',
        false: '',
      },
    },
    compoundVariants: [
      { hasDescription: true, size: 'xl', class: 'h-auto min-h-14 py-3' },
      { hasDescription: true, size: 'l', class: 'h-auto min-h-12 py-2.5' },
      { hasDescription: true, size: 'm', class: 'h-auto min-h-11 py-2' },
      { hasDescription: true, size: 's', class: 'h-auto min-h-8 py-1.5' },
      {
        variant: 'secondary',
        selected: true,
        // Figma `brand/secondary/240` — the grey wash on a selected / open
        // menu row (New-IA `132:6778` open-submenu row, `145:17174` checked
        // row). Auto-inverts in dark via the secondary palette.
        class: 'bg-secondary-200/50',
      },
      {
        variant: 'primary',
        selected: true,
        class: 'bg-primary-50 ',
      },
      {
        variant: 'error',
        selected: true,
        class: 'bg-red-50 ',
      },
      { disabled: true, class: '!opacity-30 web:pointer-events-none' },
    ],
    defaultVariants: {
      variant: 'secondary',
      size: 'l',
      selected: false,
      disabled: false,
      fullWidth: true,
      hasDescription: false,
      slidingHighlight: false,
    },
  },
);

export const menuListItemDescriptionVariants = cva(
  'whitespace-nowrap text-left text-fg-subtle font-medium native:font-mona-medium',
  {
    variants: {
      size: {
        xl: 'text-b2',
        l: 'text-b2',
        m: 'text-b3',
        s: 'text-b3',
      },
    },
    defaultVariants: { size: 'l' },
  },
);

/**
 * Accent overlay classes
 */
export const menuListItemAccentVariants = cva(
  'web:hover:!border-[var(--menu-accent)] ' +
    'web:hover:!bg-[color-mix(in_srgb,var(--menu-accent)_10%,transparent)] ' +
    'web:active:!border-[var(--menu-accent)] ' +
    'web:active:!bg-[color-mix(in_srgb,var(--menu-accent)_15%,transparent)]',
  {
    variants: {
      selected: {
        true:
          'web:!border-[var(--menu-accent)] ' +
          'web:!bg-[color-mix(in_srgb,var(--menu-accent)_15%,transparent)]',
        false: '',
      },
    },
    defaultVariants: { selected: false },
  },
);

/**
 * Text classes per variant + size.
 */
export const menuListItemTextVariants = cva('whitespace-nowrap text-left', {
  variants: {
    variant: {
      secondary: 'text-secondary-600 ',
      primary:
        'text-secondary-600  ' +
        // The label's OWN hover/active are web-only. On native NativeWind
        // implements `active:` by attaching press handlers to the element,
        // and a Text with press handlers claims the responder: taps on the
        // label's glyph box flashed the row and never fired its `onPress`
        // (the language picker, 2026-09-09). The `group-*` pair keeps the
        // pressed/hovered colour on native — its handlers sit on the ROW.
        'web:hover:text-primary-600 web:active:text-primary-600 ' +
        'group-hover:text-primary-600 group-active:text-primary-600 ',
      error: 'text-red-600 ',
    },
    size: {
      xl: 'text-h2 font-semibold native:font-mona-semibold',
      l: 'text-h3 font-semibold native:font-mona-semibold',
      m: 'text-h4 font-semibold native:font-mona-semibold',
      s: 'text-b3 font-medium   native:font-mona-medium',
    },
    selected: {
      true: '',
      false: '',
    },
    disabled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'primary',
      selected: true,
      class: 'text-primary-600 ',
    },
  ],
  defaultVariants: {
    variant: 'secondary',
    size: 'l',
    selected: false,
    disabled: false,
  },
});

export type MenuListItemVariantProps = VariantProps<typeof menuListItemVariants>;
